'use client';

import { ChevronIcon } from '@insta-club/components';
import { cn } from '@insta-club/utilities';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, useState } from 'react';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';
import { useFormContext } from 'react-hook-form';
import DatePicker from 'react-multi-date-picker';
import CalenderIcon from 'src/libs/components/icons/calender';

const weekDays = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];

const containerVariants = cva(
    'w-full relative rounded-lg border px-3 flex items-center transition-all duration-200 bg-white cursor-pointer select-none',
    {
        variants: {
            variant: {
                default: 'border-gray-300 hover:border-primary-500 focus-within:border-primary-500',
                error: 'border-red-500 hover:border-red-500 focus-within:border-red-500'
            },
            size: {
                default: 'h-14',
                sm: 'h-12',
                lg: 'h-16'
            }
        },
        defaultVariants: {
            variant: 'default',
            size: 'default'
        }
    }
);

const floatingLabelVariants = cva(
    'absolute right-3 bg-white px-1 text-gray-500 transition-all duration-200 pointer-events-none',
    {
        variants: {
            variant: {
                default: 'peer-focus:text-primary-500',
                error: 'text-red-500 peer-focus:text-red-500'
            },
            size: {
                default:
                    'top-4 text-sm peer-focus:-top-2 peer-focus:text-xs peer-[:not(.placeholder-shown)]:-top-2 peer-[:not(.placeholder-shown)]:text-xs',
                sm: 'top-3 text-sm peer-focus:-top-2 peer-focus:text-xs peer-[:not(.placeholder-shown)]:-top-2 peer-[:not(.placeholder-shown)]:text-xs',
                lg: 'top-5 text-base peer-focus:-top-2 peer-focus:text-xs peer-[:not(.placeholder-shown)]:-top-2 peer-[:not(.placeholder-shown)]:text-xs'
            }
        },
        defaultVariants: {
            variant: 'default',
            size: 'default'
        }
    }
);

export interface CustomDatePickerProps extends VariantProps<typeof containerVariants> {
    name: string;
    label?: string;
    containerClassName?: string;
}

const CustomDatePicker = forwardRef<HTMLDivElement, CustomDatePickerProps>(
    ({ name, label = 'تاریخ تولد', variant, size, containerClassName }, ref) => {
        const {
            setValue,
            watch,
            formState: { errors }
        } = useFormContext();

        const [isFocused, setIsFocused] = useState(false);
        const value = watch(name);
        const error = errors[name];
        const inputVariant = error ? 'error' : variant;

        return (
            <div className='flex w-full flex-col gap-1'>
                <div className='relative w-full'>
                    <DatePicker
                        value={value ? new Date(value * 1000) : ''}
                        onChange={(date) => setValue(name, date?.isValid ? date?.unix : '')}
                        onOpen={() => setIsFocused(true)}
                        onClose={() => setIsFocused(false)}
                        weekDays={weekDays}
                        calendar={persian}
                        locale={persian_fa}
                        calendarPosition='top-center'
                        showOtherDays
                        monthYearSeparator=' '
                        containerClassName='!w-full' // کل wrapper داخلی full width
                        className='!w-full' // input خود DatePicker full width
                        style={{ width: '100%' }} // اطمینان بیشتر
                        render={(val, openCalendar) => (
                            <div
                                ref={ref}
                                onClick={openCalendar}
                                className={cn(
                                    containerVariants({ variant: inputVariant, size }),
                                    'peer w-full', // container اصلی full width
                                    containerClassName
                                )}
                            >
                                <CalenderIcon width={20} height={20} className='shrink-0 text-gray-700' />
                                <div className='mx-2 h-[60%] w-px shrink-0 bg-gray-200' />
                                <div
                                    className={cn(
                                        'flex-1 truncate text-right text-sm text-gray-800 placeholder-shown:text-transparent',
                                        !val && 'placeholder-shown'
                                    )}
                                >
                                    {val || ''}
                                </div>
                                <ChevronIcon width={16} height={16} className='shrink-0 text-gray-700' />
                                {label && (
                                    <label
                                        className={cn(
                                            floatingLabelVariants({ variant: inputVariant, size }),
                                            (val || isFocused) && '-top-2 text-xs'
                                        )}
                                    >
                                        {label}
                                    </label>
                                )}
                            </div>
                        )}
                    />
                </div>
                {error && <p className='text-right text-sm text-red-500'>{error?.message as string}</p>}
            </div>
        );
    }
);

CustomDatePicker.displayName = 'CustomDatePicker';
export default CustomDatePicker;

